import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { ArrowUp, ArrowDown, Minus, X, Repeat, AlertTriangle, Undo2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const MOVE_STYLE = {
  up: "bg-[#009E60]/10 border-l-4 border-[#009E60]",
  down: "bg-red-50 border-l-4 border-red-500",
  out: "bg-gray-100 border-l-4 border-gray-400 opacity-60",
  stay: "border-l-4 border-transparent",
};

// Default moves from per-pair counts: bottom N of upper go down, top N of lower go up
const defaultMoves = (divisions, counts) => {
  const m = {};
  divisions.forEach((d) => d.table.forEach((r) => { m[r.team_id] = "stay"; }));
  divisions.slice(0, -1).forEach((upper, i) => {
    const lower = divisions[i + 1];
    const n = counts[i] || 0;
    if (!n) return;
    upper.table.slice(-n).forEach((r) => { m[r.team_id] = "down"; });
    lower.table.slice(0, n).forEach((r) => { m[r.team_id] = "up"; });
  });
  return m;
};

const buildLineups = (divisions, moves) => {
  const out = Object.fromEntries(divisions.map((d) => [d.competition_id, []]));
  divisions.forEach((d, i) => d.table.forEach((r) => {
    const mv = moves[r.team_id];
    if (mv === "out") return;
    const target = mv === "up" ? divisions[i - 1] : mv === "down" ? divisions[i + 1] : d;
    out[(target || d).competition_id].push(r.team_id);
  }));
  return out;
};

const MoveButtons = ({ value, onChange, canUp, canDown, teamId }) => (
  <div className="flex gap-1">
    {[["up", ArrowUp, canUp, "Promote"], ["stay", Minus, true, "Stay"], ["down", ArrowDown, canDown, "Relegate"], ["out", X, true, "Not playing next season"]].map(([k, Icon, ok, title]) => ok && (
      <button key={k} type="button" title={title} onClick={() => onChange(k)}
        className={`w-7 h-7 flex items-center justify-center border transition-colors ${value === k ? "bg-[#09090b] text-white border-[#09090b]" : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"}`}
        data-testid={`move-${k}-${teamId}`}>
        <Icon className="w-3.5 h-3.5" />
      </button>
    ))}
  </div>
);

const DivisionTable = ({ div, index, total, moves, setMove, nextCount }) => (
  <div className="bg-white border border-gray-200" data-testid={`rollover-division-${div.competition_id}`}>
    <div className="px-4 py-3 bg-[#09090b] text-white flex items-center justify-between">
      <h3 className="font-heading font-bold uppercase">{div.name}</h3>
      <span className="text-xs text-gray-300" data-testid={`next-count-${div.competition_id}`}>{div.table.length} now → <b className="text-[#FFC72C]">{nextCount}</b> next season</span>
    </div>
    {(div.unplayed_matches > 0 || !div.played_any) && (
      <p className="px-4 py-2 text-xs bg-orange-50 text-orange-700 flex items-center gap-2" data-testid={`warning-${div.competition_id}`}>
        <AlertTriangle className="w-3.5 h-3.5" />
        {!div.played_any ? "No results recorded — table order is alphabetical, not final." : `${div.unplayed_matches} match(es) still unplayed.`}
      </p>
    )}
    <table className="w-full text-sm">
      <tbody>
        {div.table.map((r) => (
          <tr key={r.team_id} className={`border-b border-gray-100 ${MOVE_STYLE[moves[r.team_id]]}`} data-testid={`rollover-row-${r.team_id}`}>
            <td className="py-2 pl-3 w-8 font-bold text-gray-400">{r.position}</td>
            <td className="py-2"><span className="font-medium">{r.name}</span></td>
            <td className="py-2 text-xs text-gray-500 w-20">{r.played} P · {r.points} pts</td>
            <td className="py-2 pr-3 w-36">
              <MoveButtons value={moves[r.team_id]} onChange={(v) => setMove(r.team_id, v)} canUp={index > 0} canDown={index < total - 1} teamId={r.team_id} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const History = ({ items, onUndo }) => items.length > 0 && (
  <div className="bg-white border border-gray-200 p-5" data-testid="rollover-history">
    <h3 className="font-heading font-bold uppercase mb-3">Rollover History</h3>
    {items.map((h) => (
      <div key={h.rollover_id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0 text-sm" data-testid={`rollover-history-${h.rollover_id}`}>
        <span><b>{h.season} → {h.next_season}</b> · {new Date(h.at).toLocaleString()} · {h.by}{h.undone && <span className="ml-2 text-xs text-gray-400 uppercase">undone</span>}</span>
        {!h.undone && (
          <Button size="sm" variant="outline" className="rounded-none" onClick={() => onUndo(h)} data-testid={`undo-rollover-${h.rollover_id}`}>
            <Undo2 className="w-4 h-4 mr-1" /> Undo
          </Button>
        )}
      </div>
    ))}
  </div>
);

const AdminSeasonRollover = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [counts, setCounts] = useState([]);
  const [moves, setMoves] = useState({});
  const [history, setHistory] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const [p, h] = await Promise.all([
        axios.get(`${API}/admin/season-rollover/preview`, { withCredentials: true }),
        axios.get(`${API}/admin/season-rollover/history`, { withCredentials: true }),
      ]);
      setData(p.data);
      setHistory(h.data);
      const c = p.data.pairs.map((x) => x.count);
      setCounts(c);
      setMoves(defaultMoves(p.data.divisions, c));
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load the season preview");
      axios.get(`${API}/admin/season-rollover/history`, { withCredentials: true }).then((h) => setHistory(h.data)).catch(() => {});
    }
  };
  useEffect(() => { load(); }, []);

  const lineups = useMemo(() => (data ? buildLineups(data.divisions, moves) : {}), [data, moves]);
  const balanced = useMemo(() => data && data.divisions.slice(0, -1).every((d, i) => {
    const lower = data.divisions[i + 1];
    return d.table.filter((r) => moves[r.team_id] === "down").length === lower.table.filter((r) => moves[r.team_id] === "up").length;
  }), [data, moves]);

  const latest = history.find((h) => !h.undone);
  const justStarted = data && latest?.next_season === data.season && !data.divisions.some((d) => d.played_any);

  const setCount = (i, v) => {
    const c = [...counts];
    c[i] = Math.max(0, Math.min(Number(v) || 0, 6));
    setCounts(c);
    setMoves(defaultMoves(data.divisions, c));
  };

  const execute = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/season-rollover/execute`, { season: data.season, lineups }, { withCredentials: true });
      toast.success(`${data.next_season} season created with the new line-ups`);
      setConfirm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rollover failed");
    } finally {
      setBusy(false);
    }
  };

  const undo = async (h) => {
    if (!window.confirm(`Undo the ${h.season} → ${h.next_season} rollover? The ${h.next_season} leagues will be deleted.`)) return;
    try {
      await axios.post(`${API}/admin/season-rollover/${h.rollover_id}/undo`, {}, { withCredentials: true });
      toast.success("Rollover undone");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Undo failed");
    }
  };

  return (
    <AdminLayout title="Promotion & Relegation">
      <div className="space-y-8" data-testid="admin-season-rollover">
        <p className="text-gray-500 max-w-3xl">
          End the season in one step. Final tables decide who goes up and down; adjust any club by hand, then confirm to create the next season's leagues.
          The finished season's tables, results and history are kept untouched.
        </p>

        {error && <div className="bg-white border border-gray-200 p-6 text-gray-500" data-testid="rollover-error">{error}</div>}

        {data && (
          <>
            <div className="bg-white border border-gray-200 p-5 flex flex-wrap items-end gap-6" data-testid="rollover-settings">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-gray-400">Season</p>
                <p className="font-heading text-2xl font-black" data-testid="rollover-season">{data.season} <span className="text-gray-400">→</span> <span className="text-[#009E60]">{data.next_season}</span></p>
              </div>
              {data.pairs.map((p, i) => (
                <div key={p.upper_id}>
                  <label className="text-[10px] uppercase tracking-widest text-gray-400 block">{data.divisions[i].name.replace("ABFA ", "")} ↕ {data.divisions[i + 1].name.replace("ABFA ", "")}</label>
                  <Input type="number" min={0} max={6} value={counts[i] ?? 0} onChange={(e) => setCount(i, e.target.value)} className="w-24 rounded-none" data-testid={`pair-count-${i}`} />
                </div>
              ))}
              <Button variant="outline" className="rounded-none" onClick={() => setMoves(defaultMoves(data.divisions, counts))} data-testid="reset-moves-btn">Reset to table</Button>
              <Button className="ml-auto bg-[#FFC72C] hover:bg-[#E6B325] text-black rounded-none" disabled={data.next_season_exists || !balanced || justStarted} onClick={() => setConfirm(true)} data-testid="confirm-rollover-btn">
                <Repeat className="w-4 h-4 mr-2" /> Start {data.next_season} Season
              </Button>
            </div>
            {data.next_season_exists && <p className="text-sm text-orange-600" data-testid="next-season-exists">{data.next_season} leagues already exist — undo the previous rollover first to run it again.</p>}
            {justStarted && <p className="text-sm text-[#009E60] font-medium" data-testid="just-started-note">The {data.season} season has just been started. This tool will be available again once {data.season} results are in — or undo the rollover below.</p>}
            {!balanced && <p className="text-sm text-orange-600" data-testid="unbalanced-warning">Promotions and relegations between each pair of divisions must be equal.</p>}

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
              {data.divisions.map((d, i) => (
                <DivisionTable key={d.competition_id} div={d} index={i} total={data.divisions.length} moves={moves}
                  setMove={(tid, v) => setMoves({ ...moves, [tid]: v })} nextCount={lineups[d.competition_id]?.length || 0} />
              ))}
            </div>
            {data.other_leagues.length > 0 && (
              <p className="text-sm text-gray-500" data-testid="other-leagues-note">
                Also copied to {data.next_season} with the same clubs: {data.other_leagues.map((o) => o.name).join(", ")}.
              </p>
            )}
          </>
        )}

        <History items={history} onUndo={undo} />

        <AlertDialog open={confirm} onOpenChange={setConfirm}>
          <AlertDialogContent data-testid="rollover-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading uppercase">Start the {data?.next_season} season?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-1 text-sm">
                  {data?.divisions.map((d) => (
                    <p key={d.competition_id}><b>{d.name}</b>: {lineups[d.competition_id]?.length} clubs</p>
                  ))}
                  <p className="pt-2 flex items-center gap-1 text-gray-500"><CheckCircle2 className="w-4 h-4 text-[#009E60]" /> {data?.season} tables and results are kept. You can undo until the first {data?.next_season} match is created.</p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none" data-testid="rollover-cancel-btn">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={(e) => { e.preventDefault(); execute(); }} disabled={busy} className="bg-[#09090b] rounded-none" data-testid="rollover-execute-btn">
                {busy ? "Working..." : "Confirm"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminSeasonRollover;
